import { NavLink, useLocation } from 'react-router-dom';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import cn from 'classnames';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import {
  STUDIO_ROUTE,
  FIGMA_TO_STORYCARDS_ROUTE,
  ANALYTICS_ROUTE,
  SEGMENTS_ROUTE,
  FLOW_ROUTE,
  DATA_AND_PRIVACY_ROUTE,
  SUBSCRIPTION_PLANS_PAGE,
  CARDS_FORMAT_ROUTE,
  COLLABORATION_ROUTE,
  ABOUT_ROUTE,
  ROOT_ROUTE,
  BRAND_COLOR,
  INSPIRATION_LINK,
  AI_ROUTE,
  ENGAGEMENT_AI_ROUTE,
  BRAND_GOVERNANCE_ROUTE,
  INTEGRATIONS_ROUTE,
  HOSTING_ROUTE,
  SECURITY_ROUTE,
  API_ROUTE,
  INFOGRAPHICS_LINK,
  INSURANCE_SOLUTIONS_ROUTE,
  BANKING_SOLUTIONS_ROUTE,
  PAYMENTS_SOLUTIONS_ROUTE,
  HEALTHCARE_SOLUTIONS_ROUTE,
  REAL_ESTATE_SOLUTIONS_ROUTE,
  RETAIL_SOLUTIONS_ROUTE,
  TELECOMMUNICATIONS_SOLUTIONS_ROUTE,
  AGENCIES_ROUTE,
  PUBLISHERS_ROUTE,
} from 'components/constants';
import icons from 'assets/icons';
import useResponsiveQuery from 'utils/useResponsiveQuery';
import { STORYCARDS_APP } from 'components/constants';
import { dataLayerEvents } from 'utils/gtag';
import translator from 'utils/translator';
import DropdownMenu, { DropdownId } from 'components/common/Header/DropdownMenu';
import MobileLogo from './MobileLogo';
import { LinkWithQuery } from '../LinkWithQuery';
import HamburgerIcon from 'components/icons/HamburgerIcon';
import styles from './styles.module.scss';

type Props = {
  invertColors?: boolean;
  isTransparentHeader?: boolean;
  isPricingPage?: boolean;
  isWhite?: boolean;
  isMarketingHome?: boolean;
};
interface MenuItemType {
  label: string;
  url?: string;
  dropdown?: { label: string; subtitle?: string; url: string; external?: boolean; target?: string }[];
  dropdownId?: DropdownId;
  target?: string;
  hideOnDesk?: boolean;
  colls?: number[];
  groupLabels?: string[];
  panelDescription?: string;
}

const _t = (path: string, pattern: object = {}) => translator.text(`${path}`, pattern);

const menus: MenuItemType[] = [
  {
    label: _t('menu.solutions'),
    dropdownId: 'solutions',
    dropdown: [
      { label: _t('menu.insurance.title'), subtitle: _t('menu.insurance.subtitle'), url: INSURANCE_SOLUTIONS_ROUTE },
      { label: _t('menu.banking.title'), subtitle: _t('menu.banking.subtitle'), url: BANKING_SOLUTIONS_ROUTE },
      { label: _t('menu.payments.title'), subtitle: _t('menu.payments.subtitle'), url: PAYMENTS_SOLUTIONS_ROUTE },
      { label: _t('menu.healthcare.title'), subtitle: _t('menu.healthcare.subtitle'), url: HEALTHCARE_SOLUTIONS_ROUTE },
      { label: _t('menu.realEstate.title'), subtitle: _t('menu.realEstate.subtitle'), url: REAL_ESTATE_SOLUTIONS_ROUTE },
      { label: _t('menu.retail.title'), subtitle: _t('menu.retail.subtitle'), url: RETAIL_SOLUTIONS_ROUTE },
      {
        label: _t('menu.telecommunications.title'),
        subtitle: _t('menu.telecommunications.subtitle'),
        url: TELECOMMUNICATIONS_SOLUTIONS_ROUTE,
      },
      { label: 'Advertising agencies', subtitle: 'Build and deliver interactive client work.', url: AGENCIES_ROUTE },
      { label: 'Publishers & media', subtitle: 'Create editorial and commercial products.', url: PUBLISHERS_ROUTE },
    ],
    colls: [3, 4, 2],
    groupLabels: ['Financial services', 'Customer industries', 'Agencies & media'],
    panelDescription: 'Explore the products and operating model Storycards supports in your industry.',
  },
  {
    label: _t('menu.platform'),
    dropdownId: 'product',
    dropdown: [
      { label: _t('menu.aiCreation.title'), subtitle: _t('menu.aiCreation.subtitle'), url: AI_ROUTE },
      { label: _t('menu.aiEngine.title'), subtitle: _t('menu.aiEngine.subtitle'), url: ENGAGEMENT_AI_ROUTE },
      { label: _t('menu.editor.title'), subtitle: _t('menu.editor.subtitle'), url: STUDIO_ROUTE },
      {
        label: 'Figma to Storycards',
        subtitle: 'Turn design comments into editable interactions.',
        url: FIGMA_TO_STORYCARDS_ROUTE,
      },
      { label: _t('menu.flow.title'), subtitle: _t('menu.flow.subtitle'), url: FLOW_ROUTE },
      { label: _t('menu.cardsFormat.title'), subtitle: _t('menu.cardsFormat.subtitle'), url: CARDS_FORMAT_ROUTE },
      {
        label: _t('menu.infographics.title'),
        subtitle: _t('menu.infographics.subtitle'),
        url: INFOGRAPHICS_LINK,
        external: true,
        target: '_blank',
      },
      {
        label: _t('menu.brandGovernance.title'),
        subtitle: _t('menu.brandGovernance.subtitle'),
        url: BRAND_GOVERNANCE_ROUTE,
      },
      { label: _t('menu.collaboration.title'), subtitle: _t('menu.collaboration.subtitle'), url: COLLABORATION_ROUTE },
      { label: _t('menu.hosting.title'), subtitle: _t('menu.hosting.subtitle'), url: HOSTING_ROUTE },
      { label: _t('menu.security.title'), subtitle: _t('menu.security.subtitle'), url: SECURITY_ROUTE },
    ],
    colls: [7, 4],
    groupLabels: ['Create', 'Govern & scale'],
    panelDescription: 'Create with AI, perfect every detail and operate every product with enterprise control.',
  },
  {
    label: _t('menu.dataAndOptimization'),
    dropdownId: 'analytics',
    dropdown: [
      { label: _t('menu.analytics.title'), subtitle: _t('menu.analytics.subtitle'), url: ANALYTICS_ROUTE },
      {
        label: _t('menu.dataCollection.title'),
        subtitle: _t('menu.dataCollection.subtitle'),
        url: DATA_AND_PRIVACY_ROUTE,
      },
      { label: _t('menu.segments.title'), subtitle: _t('menu.segments.subtitle'), url: SEGMENTS_ROUTE },
      {
        label: _t('menu.integrations.title'),
        subtitle: _t('menu.integrations.subtitle'),
        url: INTEGRATIONS_ROUTE,
      },
      {
        label: _t('menu.api.title'),
        subtitle: _t('menu.api.subtitle'),
        url: API_ROUTE,
      },
    ],
    colls: [3, 2],
    groupLabels: ['Understand & improve', 'Connect'],
    panelDescription: 'Collect structured first-party data, understand behavior and continuously improve performance.',
  },
  { label: _t('menu.about'), url: ABOUT_ROUTE, hideOnDesk: true },
  // { label: _t('menu.pricing'), url: PRICING_ROUTE },
  { label: _t('menu.inspiration'), url: INSPIRATION_LINK, target: '_blank' },
];

function Header({ invertColors, isPricingPage, isWhite = true, isMarketingHome = false }: Props) {
  const isMobile = useResponsiveQuery(880);
  const [navbarShown] = useState(false);
  const [isMinimalHeader, setIsMinimalHeader] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdownId, setActiveDropdownId] = useState<MenuItemType['dropdownId']>();
  const ref = useRef<HTMLElement>(null);
  const scrollHeight = useRef(1);
  const onOrientationChange = () => {
    setIsMenuOpen(false);
  };

  const { search } = useLocation();
  const urlParams = new URLSearchParams(search);
  if (document.referrer) urlParams.append('referrer', document.referrer);
  const pricingUrlParamsStr = urlParams.toString().length > 0 ? `?${urlParams.toString()}` : '';
  const pricingUrl = `${SUBSCRIPTION_PLANS_PAGE}${pricingUrlParamsStr}`;

  useEffect(() => {
    const onScroll = () => {
      const nextIsMinimal = window.scrollY > (isMarketingHome ? 24 : 0);
      setIsMinimalHeader(nextIsMinimal);
    };

    const onWindowResize = () => {
      scrollHeight.current = document.body.scrollHeight;
    };

    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onWindowResize);
    onWindowResize();
    onScroll();

    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onWindowResize);
    };
  }, [isMobile, isMarketingHome]);

  // disable content scrolling if menu opened
  useEffect(() => {
    if (isMenuOpen) {
      disableBodyScroll(document.body);
      document.documentElement.style.cssText = 'height:100vh !important';

      document.documentElement.classList.add(`${styles.disableScroll}`);
    } else {
      enableBodyScroll(document.body);
      document.documentElement.style.cssText = 'height: auto !important';

      document.documentElement.classList.remove(`${styles.disableScroll}`);
    }
  }, [isMenuOpen]);

  // Fix bug with change orientation
  useEffect(() => {
    window.addEventListener('orientationchange', onOrientationChange);

    return () => {
      window.removeEventListener('orientationchange', onOrientationChange);
    };
  }, []);

  const handleMenu = () => {
    setIsMenuOpen((isOpen) => {
      if (isOpen) setActiveDropdownId(undefined);
      return !isOpen;
    });
  };

  const renderMenuLinks = () => {
    return menus.map((item) => {
      if (!isMobile && item.hideOnDesk) return null;
      if (item.url && item.target === '_blank') {
        return (
          <a
            key={`menu-item-${item.dropdownId}-${item.url}`}
            href={item.url}
            target="_blank"
            rel="noreferrer"
            className={cn(
              styles.navLink,
              !isWhite && styles.whiteText,
              isMinimalHeader && isWhite && styles.blackText,
            )}
            onClick={isMobile ? () => setIsMenuOpen(false) : undefined}
          >
            {item.label}
          </a>
        );
      }
      return !item.url && item.dropdown ? (
        <DropdownMenu
          key={item.label}
          invertColors={invertColors}
          title={item.label}
          titleClassName={cn(
            styles.navLink,
            !isWhite && styles.whiteText,
            isMinimalHeader && isWhite && styles.blackText,
            'cursor-pointer',
          )}
          dropdownLinks={item.dropdown}
          id={item.dropdownId}
          activeDropdownId={activeDropdownId}
          onClick={setActiveDropdownId}
          colls={item.colls}
          groupLabels={item.groupLabels}
          panelDescription={item.panelDescription}
          onNavigate={() => {
            setActiveDropdownId(undefined);
            if (isMobile) setIsMenuOpen(false);
          }}
        />
      ) : (
        <NavLink
          to={`${item.url}${search}`}
          key={item.label}
          activeClassName={styles.navLinkActive}
          className={cn(
            styles.navLink,
            !isWhite && styles.whiteText,
            isMinimalHeader && isWhite && styles.blackText,
            'cursor-pointer',
          )}
          onClick={isMobile ? () => setIsMenuOpen(false) : undefined}
        >
          {item.label}
        </NavLink>
      );
    });
  };

  return (
    <header
      ref={ref}
      className={cn(
        styles.header,
        !isMobile && isWhite && styles.whiteHeader,
        !isMobile && isMinimalHeader && styles.minimal,
        'flex',
        'fixed',
        'duration-300',
        'z-40',
        'w-full',
        'items-center',
        {
          [styles.openedMobileMenu]: isMenuOpen,
          [styles.inverted]: invertColors,
          [styles.marketingHome]: isMarketingHome,
          'shadow-md': navbarShown,
        },
      )}
      id="header"
    >
      <div className={cn('flex', 'w-full', 'items-center')}>
        <div className={cn('flex', 'flex-1', 'items-center')}>
          <div className={cn('flex', 'relative')}>
            {isMobile ? (
              <MobileLogo full={isMarketingHome} compact={isMarketingHome && isMinimalHeader} />
            ) : (
              <>
      <div
                  className={cn(
                    'flex-1',
                    'duration-300',
                    styles.bigLogo,
                    !isMobile && isMinimalHeader && styles.hidden,
                  )}
                  onClick={() => dataLayerEvents.onHeaderLogoClick()}
                >
                  <LinkWithQuery to={ROOT_ROUTE}>
                    <img
                      className={`${styles.logo} `}
                      src={
                        isMarketingHome
                          ? icons.storycardsLogoTrim
                          : isWhite
                          ? icons.storycardsLogo
                          : icons.storycardsLogoWhite
                      }
                      title="Storycards"
                      alt="Storycards"
                    />
                  </LinkWithQuery>
      </div>
      <div
                  className={cn(
                    'flex-1',
                    'duration-300',
                    styles.smallLogo,
                    !isMobile && isMinimalHeader && styles.visible,
                  )}
                  onClick={() => dataLayerEvents.onHeaderLogoClick()}
                >
                  <LinkWithQuery to={ROOT_ROUTE} className={styles.littleLogo}>
                    <img
                      className={styles.logoNoText}
                      src={icons.storycardsLogoNoText}
                      title="Storycards"
                      alt="Storycards"
                      itemProp="image"
                    />
                  </LinkWithQuery>
      </div>
              </>
            )}

            {!isMobile && (
              <div className={cn(styles.menu, 'flex', 'flex-1', 'items-center', 'relative', 'whitespace-nowrap')}>
                {renderMenuLinks()}
              </div>
            )}
          </div>
        </div>

        {!isMobile && (
          <a
            href={STORYCARDS_APP}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(styles.loginButton, !isWhite && styles.whiteBorder, 'rounded-full', 'font-semibold')}
            onClick={() => dataLayerEvents.onHeaderLoginClick()}
          >
            {_t('header.loginBtnLabel')}
          </a>
        )}
        {!isMobile && !isPricingPage && (
          <a
            href={pricingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(styles.createButton, 'text-white', 'rounded-full', 'font-semibold')}
            onClick={() => dataLayerEvents.onHeaderStartCreatingClick()}
          >
            Start Creating
          </a>
        )}

        {isMobile && (
          <>
            <div className={styles.hamburgerWrapper}>
              {isPricingPage ? (
                <a
                  href={STORYCARDS_APP}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${styles.loginButton} ${styles.loginButtonMobile} rounded-full font-semibold`}
                  style={{
                    backgroundColor: invertColors ? 'inherit' : '#fff',
                    color: BRAND_COLOR,
                    border: !navbarShown ? 'solid 1px #CDCDCD' : 'solid 1px #ccc',
                  }}
                  onClick={() => dataLayerEvents.onHeaderLoginClick()}
                >
                  {_t('header.loginBtnLabel')}
                </a>
              ) : !isMenuOpen ? (
                <a
                  href={pricingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${styles.loginButton} ${styles.startButtonMobile} rounded-full font-semibold`}
                  style={{
                    backgroundColor: BRAND_COLOR,
                    color: '#FFF',
                    border: 'none',
                  }}
                  onClick={() => dataLayerEvents.onHeaderLoginClick()}
                >
                  {_t('header.startBtnLabelMobile')}
                </a>
              ) : (
                <a
                  href={STORYCARDS_APP}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${styles.loginButton} ${styles.loginButtonMobile} rounded-full font-semibold`}
                  style={{
                    backgroundColor: invertColors ? 'inherit' : '#fff',
                    color: BRAND_COLOR,
                    border: 'solid 1px #CDCDCD',
                  }}
                  onClick={() => dataLayerEvents.onHeaderLoginClick()}
                >
                  {_t('header.loginBtnLabel')}
                </a>
              )}

              <button className={`${styles.hamburgerButton} `} onClick={handleMenu}>
                {isMenuOpen ? (
                  <span className={styles.closeIcon}>
                    <span className={styles.line} />
                    <span className={styles.line} />
                  </span>
                ) : (
                  <HamburgerIcon color={isMarketingHome ? '#202124' : 'white'} />
                )}
              </button>
            </div>
            {createPortal(
              <div
                className={cn(
                  styles.mobileMenu,
                  isMenuOpen && styles.open,
                  'fixed top-0 bottom-0 left-0 right-0 w-full h-full overflow-auto',
                )}
              >
                <div className="flex flex-col">{renderMenuLinks()}</div>
                {!isPricingPage && (
                  <div className={styles.mobileMenuFooter}>
                    <a
                      href={pricingUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.mobileMenuCreate}
                      onClick={() => {
                        setIsMenuOpen(false);
                        dataLayerEvents.onHeaderStartCreatingClick();
                      }}
                    >
                      Start Creating
                      <span aria-hidden="true">→</span>
                    </a>
                  </div>
                )}
              </div>,
              document.body,
            )}
          </>
        )}
      </div>
    </header>
  );
}

export default Header;

Header.defaultProps = {
  isPricingPage: false,
  isTransparentHeader: false,
  invertColors: false,
  isMarketingHome: false,
};
